/**
 * Admin/Owner-only toggle to route chat requests through ai_stream (streaming)
 * instead of ai_manager_v2 (buffered). Public users always use ai_manager_v2.
 *
 * Persistence: localStorage key `chatty:streamMode` ('on' | 'off' | null).
 * Read at call time by `src/utils/chatApi.js` along with the admin gate.
 *
 * To use the streaming path the conditions must ALL be true:
 *   1. User's custom:membership ∈ {'admin','owner'}
 *   2. localStorage.chatty:streamMode === 'on'
 *   3. modelConfig.stream === true (i.e. OPENAI_STREAM flag enabled for that model)
 */
import React, { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'chatty:streamMode';

export const isStreamModeEnabled = () => {
  try { return localStorage.getItem(STORAGE_KEY) === 'on'; }
  catch (e) { return false; }
};

const StreamModeToggle = ({ user }) => {
  const isAdminOrOwner = ['owner', 'admin'].includes(user?.['custom:membership']);
  const [enabled, setEnabled] = useState(isStreamModeEnabled());

  useEffect(() => {
    // Sync across tabs
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setEnabled(e.newValue === 'on'); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    setEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off'); } catch (err) {}
      return next;
    });
  }, []);

  if (!isAdminOrOwner) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      className="transition-all duration-200 rounded-full p-2.5 self-center hover:scale-105 chatty-glass-button"
      style={{
        color: enabled ? '#3b6ef5' : '#8a8a92',
        position: 'relative',
      }}
      title={
        enabled
          ? 'Stream mode ON — requests route to stream.limkc.com (ai_stream). Click to switch back to ai_manager_v2.'
          : 'Stream mode OFF — requests route to api.limkc.com/extended/ (ai_manager_v2). Click to enable streaming.'
      }
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        {/* Horizontal wavy lines = stream */}
        <path d="M3 8c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
        <path d="M3 14c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
      </svg>
      {enabled && (
        <span
          aria-hidden
          style={{
            position: 'absolute', bottom: 4, right: 4, width: 6, height: 6,
            borderRadius: '50%', background: '#3b6ef5',
          }}
        />
      )}
    </button>
  );
};

export default StreamModeToggle;
